import React, { useEffect, useState } from 'react';
import { CLabel, CInput, CSelect, CSwitch } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import 'react-dates/initialize';
import { DateRangePicker, SingleDatePicker } from 'react-dates';
import moment from 'moment';
import { ITableFilterProps } from '@transferz/models';

export const TableFilter: React.FunctionComponent<ITableFilterProps> = ({
  queries,
  showToggle = false,
  isToggleOn,
  setIsToggleOn = () => {},
  toggleLabel = '',
  filterByOptions,
  onFilterChange,
  label = 'Filter by:',
  placeholder = '',
  clearIcon = 'cilFilterX',
  lazy = true,
}) => {
  const [activeFilter, setActiveFilter] = useState(filterByOptions[0] as any);
  const [activeEnum, setActiveEnum] = useState('');
  const [toggleValue, setToggleValue] = useState(isToggleOn);
  const [selectOptions, setSelectOptions] = useState([]) as any;
  const [focusedDateInput, setFocusedDateInput] = useState(null) as any;
  const [value, setValue] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  }) as any;
  const [date, setDate] = useState(null) as any;
  const [focusedDate, setFocusedDate] = useState(null) as any;

  const handleFilterChange = ({ target: { value } }: any, type: string): void => {
    if ((lazy && type === 'input') || (!lazy && type === 'change')) {
      return;
    }
    setValue(value);
    onFilterChange(value, activeFilter?.key as string, '');
  };

  useEffect(() => {
    const firstDate = queries[activeFilter?.args?.fields?.[0]];
    const secondDate = queries[activeFilter?.args?.fields?.[1]];
    setDateRange({
      startDate: firstDate ? moment(firstDate) : null,
      endDate: secondDate ? moment(secondDate) : null,
    });
  }, [activeFilter]);

  useEffect(() => {
    const _selectOptions: any = filterByOptions?.find((option: any) => option.type === 'SELECT' && option.key === activeFilter?.key);
    if (_selectOptions) {
      setSelectOptions([{ key: 'ALL', label: 'All' }, ..._selectOptions]);
    }
  }, [filterByOptions, setSelectOptions, activeFilter]);

  return (
    <div className="form-inline justify-content-sm-end">
      {showToggle && (
        <>
          <CLabel className="mr-2">{toggleLabel}</CLabel>
          <CSwitch
            color="primary"
            className="d-block mr-1"
            checked={toggleValue}
            onChange={() => {
              setIsToggleOn(!toggleValue);
              setToggleValue(!toggleValue);
            }}
            size="lg"
            shape="pill"
          />
        </>
      )}
      <CLabel className="mr-2">{label}</CLabel>
      <CSelect
        name="filterBy"
        className="form-control mr-1"
        onChange={({ target: { value } }: any) => {
          const filter = filterByOptions.find(i => i.key === value);
          setActiveFilter(filter);
          setValue('');
          if (filter?.triggerQueryOnSelect) {
            onFilterChange('', value, filter?.type as string);
          }
        }}
        value={activeFilter?.key}
      >
        {filterByOptions.map(({ label, key }) => (
          <option value={key} key={key}>
            {label}
          </option>
        ))}
      </CSelect>
      {activeFilter?.type === 'DATE_RANGE' && (
        <DateRangePicker
          minimumNights={0}
          startDate={dateRange.startDate}
          startDateId="startDate"
          endDate={dateRange.endDate}
          endDateId="endDate"
          onDatesChange={({ startDate, endDate }: any) => {
            setDateRange({ startDate, endDate });
            if (startDate && endDate) {
              onFilterChange({
                startDate: moment(startDate).format('YYYY-MM-DDT00:00:00'),
                endDate: moment(endDate).format('YYYY-MM-DDT23:59:59'),
              }, activeFilter?.key, 'DATE_RANGE');
            }
          }}
          minDate={moment('2020-01-01')}
          focusedInput={focusedDateInput}
          onFocusChange={focusedInput => setFocusedDateInput(focusedInput)}
          hideKeyboardShortcutsPanel
          isOutsideRange={() => false}
          displayFormat="DD-MM-YYYY"
          small
        />
      )}
      {activeFilter?.type === 'DATE' && (
        <SingleDatePicker
          date={date}
          onDateChange={(date) => {
            setDate(date);
            onFilterChange(moment(date).format('YYYY-MM-DDTHH:mm:ss'), activeFilter?.key, 'DATE');
          }}
          focused={focusedDate}
          onFocusChange={({ focused }) => setFocusedDate(focused)}
          isOutsideRange={() => false}
          hideKeyboardShortcutsPanel
          displayFormat="DD-MM-YYYY"
          id="date"
          small
        />
      )}
      {activeFilter?.type === 'ENUM_LIST' && (
        <CSelect
          name="filterByStatus"
          className="form-control mr-1"
          onChange={({ target: { value } }: any) => {
            setActiveEnum(value);
            onFilterChange(value === 'ALL' ? '' : value, activeFilter?.key as string, 'ENUM_LIST');
          }}
          value={activeEnum}
        >
          {selectOptions.map(({ key, label }: any) => (
            <option value={key} key={key}>
              {label}
            </option>
          ))}
        </CSelect>
      )}
      {activeFilter?.type !== 'ENUM_LIST' && activeFilter?.type !== 'DATE_RANGE' && activeFilter?.type !== 'DATE' && (
        <>
          <CInput
            className={`form-control mr-1`}
            type="text"
            placeholder={placeholder}
            onInput={(e) => {
              handleFilterChange(e, 'input');
            }}
            onChange={(e) => {
              handleFilterChange(e, 'change');
            }}
            value={value}
          />
          <CIcon
            name={clearIcon}
            onClick={() => {
              onFilterChange('', activeFilter?.key as string, '');
              setValue('');
            }}
          />
        </>
      )}
    </div>
  );
};
